import React, { FunctionComponent } from "react";
import { ShimmeredDetailsList } from "@components/tables/ShimmeredDetailsList";
import {
  useCancelComponentRequest,
  useComponentsRequests,
  useRetryComponentRequest
} from "@libs/api/gateways/plt/plt-gateway.hooks";
import {
  ComponentRequestDto,
  ComponentRequestStatuses,
  GetComponentsRequestsArgs
} from "@libs/api/gateways/plt/plt-gateway.dtos";
import {
  confirm,
  IColumn,
  IconButton,
  MessageBar,
  MessageBarType,
  ScrollablePane,
  Stack
} from "@bps/fluent-ui";
import { CopyableGuid } from "@components/CopyableText";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";
import { ExpandableRow } from "@components/tables/ExpandableRow";
import { useExpandedRows } from "@components/tables/useExpandedRows";
import { ComponentText } from "../../components/components/ComponentText";
import { ComponentsRequestsDetails } from "./ComponentsRequestsDetails";
import {
  ManagerWorkflowRunInstanceLink,
  ComponentDateText
} from "../../_shared-blocks";

interface ComponentsRequestsListProps {
  filter: GetComponentsRequestsArgs;
}

export const ComponentsRequestsList: FunctionComponent<ComponentsRequestsListProps> = ({
  filter
}) => {
  const { expandedIds, toggleExpandedIds } = useExpandedRows();

  const { data: hasWritePermission } = usePermissionsCheckQuery([
    Permissions.PltComponentsWrite
  ]);

  const { isLoading, error, data = [] } = useComponentsRequests(filter, {
    refetchOnMount: "always"
  });

  const {
    mutate: retryComponentRequest,
    isLoading: retryIsLoading,
    error: retryError,
    reset: resetRetry
  } = useRetryComponentRequest();

  const {
    mutate: cancelComponentRequest,
    isLoading: cancelIsLoading,
    error: cancelError,
    reset: resetCancel
  } = useCancelComponentRequest();

  const actionError = retryError || cancelError;
  const actionErrorMessage = (actionError?.detail || actionError?.message) as
    | string
    | undefined;

  const resetActionErrors = () => {
    resetRetry();
    resetCancel();
  };

  const confirmRetry = (code: string) =>
    confirm({
      dialogContentProps: {
        title: "Retry component request",
        subText: `Are you sure you want to queue the request for ${code} again? It will run once no other request is in progress for this tenant.`
      }
    });

  const confirmCancel = (code: string) =>
    confirm({
      dialogContentProps: {
        title: "Cancel component request",
        subText: `Are you sure you want to cancel the request for ${code}? Its running workflows will be stopped and the request will be marked as Failed with the reason Cancelled.`
      }
    });

  const columns: IColumn[] = [
    {
      key: "actions",
      name: "",
      minWidth: 32,
      maxWidth: 32,
      onRender: (item: ComponentRequestDto) => (
        <IconButton
          disabled={!hasWritePermission || retryIsLoading || cancelIsLoading}
          styles={{ root: { justifySelf: "end" } }}
          menuIconProps={{ iconName: "more" }}
          menuProps={{
            items: [
              {
                key: "retry",
                text: "Retry",
                disabled: item.status !== ComponentRequestStatuses.Failed,
                onClick: () => {
                  confirmRetry(item.code).then(isConfirmed => {
                    if (isConfirmed) {
                      retryComponentRequest(item.id);
                    }
                  });
                }
              },
              {
                key: "cancel",
                text: "Cancel",
                disabled:
                  item.status !== ComponentRequestStatuses.Queued &&
                  item.status !== ComponentRequestStatuses.InProgress,
                onClick: () => {
                  confirmCancel(item.code).then(isConfirmed => {
                    if (isConfirmed) {
                      cancelComponentRequest(item.id);
                    }
                  });
                }
              }
            ]
          }}
        />
      )
    },
    {
      key: "id",
      name: "ID",
      minWidth: 200,
      maxWidth: 200,
      onRender: (item: ComponentRequestDto) => (
        <CopyableGuid value={item.id} short />
      )
    },
    {
      key: "code",
      name: "Code",
      minWidth: 200,
      maxWidth: 200,
      onRender: (item: ComponentRequestDto) => (
        <ComponentText code={item.code} />
      )
    },
    {
      key: "scope-id",
      name: "Scope ID",
      minWidth: 200,
      maxWidth: 200,
      onRender: (item: ComponentRequestDto) => (
        <CopyableGuid value={item.scopeId} short />
      )
    },
    {
      key: "action",
      name: "Action",
      minWidth: 200,
      maxWidth: 200,
      onRender: (item: ComponentRequestDto) => item.action
    },
    {
      key: "status",
      name: "Status",
      minWidth: 200,
      maxWidth: 200,
      onRender: (item: ComponentRequestDto) => item.status
    },
    {
      key: "instance-id",
      name: "WF Instance Link",
      minWidth: 200,
      maxWidth: 200,
      onRender: (item: ComponentRequestDto) =>
        item.componentManagerWorkflowRunInstanceId ? (
          <ManagerWorkflowRunInstanceLink
            id={item.componentManagerWorkflowRunInstanceId}
          />
        ) : (
          "-"
        )
    },
    {
      key: "created-date",
      name: "Created date",
      minWidth: 100,
      maxWidth: 100,
      onRender: (item: ComponentRequestDto) => (
        <ComponentDateText changeLog={item.changeLog} dateField="createdDate" />
      )
    },
    {
      key: "updated-date",
      name: "Updated date",
      minWidth: 100,
      maxWidth: 100,
      onRender: (item: ComponentRequestDto) => (
        <ComponentDateText changeLog={item.changeLog} dateField="updatedDate" />
      )
    }
  ];
  return (
    <Stack styles={{ root: { height: "100%" } }}>
      {actionErrorMessage && (
        <MessageBar
          onDismiss={resetActionErrors}
          messageBarType={MessageBarType.error}
          isMultiline
          styles={{ root: { whiteSpace: "normal", wordBreak: "break-word" } }}
        >
          {actionErrorMessage}
        </MessageBar>
      )}
      <Stack.Item grow styles={{ root: { position: "relative" } }}>
        <ScrollablePane>
          <ShimmeredDetailsList
            enableShimmer={isLoading}
            errorMessage={error?.message}
            items={data}
            columns={columns}
            stickyHeader
            onRenderRow={(props, renderDefault) => {
              if (props && renderDefault) {
                return (
                  <ExpandableRow
                    expandedIds={expandedIds}
                    toggleExpandedIds={toggleExpandedIds}
                    id={props.item.id}
                    ExpandableComponent={
                      <ComponentsRequestsDetails
                        componentsRequest={props.item}
                      />
                    }
                    rowProps={props}
                  />
                );
              }
              return null;
            }}
          />
        </ScrollablePane>
      </Stack.Item>
    </Stack>
  );
};
